import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import PackageCard from "../../components/PackageCard";
import "./PackageTransferPage.css";

/**
 * GIVING A PACKAGE AWAY — ONE PAGE, TWO HALVES (user, 2026-10-09).
 *
 * ── WHO DOES WHAT, AND WHY IT IS SPLIT THIS WAY ─────────────────────────────────────────────────
 *
 *   SENDER     arrives from the dashboard or their purchase email.
 *              Proves the package is theirs (email + phone + the 16-digit number), then names the
 *              recipient. Their part ENDS there.
 *
 *   RECIPIENT  arrives from the activation email, which carries the challenge id.
 *              Enters the code and THEIR OWN phone number, and the package moves.
 *
 * The code proves the recipient controls the mailbox it was sent to, and the phone they give is theirs.
 * Neither belongs on the sender's screen: a number typed by the person giving the package away would
 * look like an identity factor while proving nothing at all.
 *
 * ── WHY THE LINK CARRIES NO TOKEN ───────────────────────────────────────────────────────────────
 *
 * A signed token in the activation URL would make the EMAIL the key — a purchase email is exactly what
 * gets forwarded when a package is given away — so the link is a DESTINATION. The `challenge` id in it
 * moves nothing on its own: the code is in the body of the email, not in the URL, so a forwarded link
 * without the body is still useless.
 *
 * ── THE THREE STEPS ARE THE SECURITY MODEL, WHICH IS WHY THE SENDER SEES THEM ───────────────────
 *
 *   Sender:    1 prove it is yours   → 2 who is receiving it → confirm → sent
 *   Recipient: the code + their phone → confirm
 *
 * Neither half moves the package alone: the sender proves the entitlement, the recipient proves the
 * destination mailbox. A mistyped address therefore costs a code and nothing else.
 *
 * BOTH HALVES END ON A SECOND TAP (user, 2026-10-09). The recipient's guards what they TAKE ON;
 * the sender's guards what they GIVE UP, and names the address before the email leaves.
 */

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

/** The same grouping the card shows. Display only — the server strips separators before comparing. */
const groupDigits = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, "$1 ");

/** The same normalisation the server applies before it compares two addresses. */
const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

/** The same shape the browser enforces with `type="email"`, in one place, so the button and the handler agree. */
const looksLikeEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const SENDER_STEPS = [
  { id: "identify", label: "Your details" },
  { id: "recipient", label: "Recipient" },
];

/** The steps a signed-in sender walks — their session proves the package, so there is nothing to identify. */
const SENDER_STEPS_SIGNED_IN = [{ id: "recipient", label: "Recipient" }];

const PackageTransferPage = () => {
  const { purchaseId } = useParams();
  const [searchParams] = useSearchParams();

  // The recipient's challenge, from the activation email. `null` means the visitor is the sender.
  const challengeId = searchParams.get("activate");
  const isRecipient = Boolean(challengeId);

  const [step, setStep] = useState(
    isRecipient
      ? "activate"
      : // A SIGNED-IN HOLDER SKIPS THE THREE FACTORS (reported 2026-10-19: *"a signed in customer has to enter
        // all of those information again … is that a security thing or what"*). Their session IS the proof — the
        // transfer route takes `req.user` and only demands the phone/number factors from a GUEST (the same rule
        // the guest claim uses). Asking a signed-in customer to retype what the account already proves is not
        // extra security, it is friction that teaches them the factors are meaningless.
        typeof window !== "undefined" && localStorage.getItem("ogestyle_token")
        ? "recipient"
        : "identify",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /**
   * ── THE PACKAGE ITSELF, FOR A SIGNED-IN SENDER (reported 2026-10-19: *"during transfer the info on the
   * package is not included"*).
   *
   * The card was drawn from the typed number alone, so it showed no name, no expiry and no sessions — the sender
   * was asked to give something away that the page would not describe. A signed-in sender can read the purchase
   * they hold, so the card shows the REAL package. A GUEST cannot read somebody's purchase by id, so their card
   * stays as it was: the number they typed, and nothing invented.
   */
  const [purchasePreview, setPurchasePreview] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token || !purchaseId || isRecipient) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/packages/purchase/${purchaseId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setPurchasePreview(data.purchase || null);
      } catch (err) {
        // A preview that cannot be read shows the card without details — never an invented one.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [purchaseId, isRecipient]);

  // A SECOND TAP before the package moves.
  //
  // IT IS ON BOTH HALVES (user's report, 2026-10-09). It used to be the recipient's alone, on the reasoning
  // that the sender's tap moves nothing: sending a code changes nothing by itself. That is true and beside the
  // point, because the sender's tap is the one that DECIDES — it names the address the package is going to and
  // mails a stranger, and from that moment the sender has no further say in when it moves. The recipient's
  // confirmation guards what they receive; this one guards what the sender gives up, and it is asked while the
  // address is still on screen rather than after the email has gone.
  const [confirming, setConfirming] = useState(false);

  // The sender's three factors.
  const [email, setEmail] = useState("");
  const [phoneLast4, setPhoneLast4] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // The recipient, and where the code went.
  const [toEmail, setToEmail] = useState("");
  const [destination, setDestination] = useState(null);

  // The recipient's answer.
  const [code, setCode] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [movedTo, setMovedTo] = useState(null);

  const headers = () => {
    // A signed-in sender is taken on their session; a guest sends nothing and is taken on the factors.
    const token = localStorage.getItem("ogestyle_token");
    return token
      ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
      : { "Content-Type": "application/json" };
  };

  const post = async (path, body) => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, data };
  };

  /** Sender, step 1: checked here so a typo is answered before the server is asked. */
  const goToRecipient = (event) => {
    event.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Enter the email address you bought the package with.");
      return;
    }
    // ── FOUR DIGITS ARE REQUIRED, AND "BLANK" IS GONE (user, 2026-10-09) ────────────────────────
    //
    // This used to be optional for one reason: a package that had been GIVEN AWAY had no holder phone,
    // because the transfer cleared the sender's. That reason no longer exists — the recipient now supplies
    // THEIR OWN number when they accept, so every holder has one on record, and a blank can never match a
    // package that has digits. Leaving the field optional therefore did not make a gift easier to pass on;
    // it only let somebody submit a value that was guaranteed to fail.
    if (phoneLast4.replace(/\D/g, "").length !== 4) {
      setError(
        "Enter the last 4 digits of the phone on this package. If you were given it, use the number you gave when you accepted it.",
      );
      return;
    }
    if (accountNumber.replace(/\D/g, "").length !== 16) {
      setError(
        "Enter the 16-digit package number. It is on the email you received when you bought the package.",
      );
      return;
    }

    setConfirming(false);
    setStep("recipient");
  };

  /** Sender, step 2: prove the factors AND send the code. The transfer does not move here. */
  const startTransfer = async (event) => {
    event.preventDefault();
    setError("");

    // AN ADDRESS IS REQUIRED BEFORE THE CONFIRMATION IS SHOWN, and this guard is here rather than only on the
    // button because the form submits on Enter as well. Without it the confirmation rendered
    // "A code and a link go to ." — found in the browser on 2026-10-09 — which is a question with no subject,
    // and the request that followed would have carried an empty address to the server.
    if (!looksLikeEmail(toEmail)) {
      setError("Enter the email address the package is going to.");
      return;
    }

    // ── A PACKAGE CANNOT BE GIVEN TO THE ADDRESS THAT ALREADY HOLDS IT (user, 2026-10-09) ─────────────
    //
    // The server refuses this — `TRANSFER_REASON.SAME_EMAIL` — and that stays the rule. But without this the
    // sender only discovered it AFTER a code had been sent, the recipient had been emailed, and the attempt
    // had been written to the challenge table: a fat-fingered address cost a stranger an email and cost the
    // sender a wait, to learn something that was knowable from the two fields already on screen.
    //
    // The comparison is against the email the sender proved the package with, because on this page that IS
    // the package's address. The normalisation matches the server's (trim + lowercase), so `Bob@x.com` and
    // `bob@x.com` are one address here as well as there and the two cannot disagree.
    if (normalizeEmail(toEmail) === normalizeEmail(email)) {
      setConfirming(false);
      setError(
        "This package already belongs to that email address. Enter a different address to give it away.",
      );
      return;
    }

    // THE ENTER KEY CANNOT SKIP THE CONFIRMATION.
    if (!confirming) {
      setConfirming(true);
      return;
    }

    setBusy(true);

    try {
      const { ok, data } = await post(
        `/api/packages/purchase/${encodeURIComponent(purchaseId)}/transfer/start`,
        {
          toEmail: toEmail.trim(),
          email: email.trim(),
          phoneLast4: phoneLast4.replace(/\D/g, ""),
          accountNumber: accountNumber.replace(/\D/g, ""),
        },
      );

      if (!ok) {
        // Written for a person by the server: it names what to supply rather than saying "not authorized".
        setError(data.message || "We could not start the transfer.");
        return;
      }

      if (data.emailDelivered === false) {
        setError(
          "We could not send the code to that address. Check it and try again.",
        );
        return;
      }

      // THE SENDER'S PART ENDS HERE — the recipient finishes it.
      setDestination(data.destination || toEmail.trim());
      setStep("sent");
    } catch (err) {
      setError("We could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  /** Recipient: the code, their own number, and the package moves. */
  const confirmTransfer = async (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);

    try {
      const { ok, data } = await post(
        `/api/packages/purchase/${encodeURIComponent(purchaseId)}/transfer/confirm`,
        {
          challengeId,
          otpCode: code.replace(/\D/g, ""),
          phone: recipientPhone.trim(),
        },
      );

      if (!ok) {
        setError(data.message || "We could not complete the transfer.");
        return;
      }

      setMovedTo(data.purchase?.customerEmail || "you");
      setStep("done");
    } catch (err) {
      setError("We could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // A signed-in sender has ONE step: there is nothing to identify, because the session already does.
  const signedIn = typeof window !== "undefined" && Boolean(localStorage.getItem("ogestyle_token"));
  const senderSteps = signedIn ? SENDER_STEPS_SIGNED_IN : SENDER_STEPS;
  const currentIndex = senderSteps.findIndex((entry) => entry.id === step);
  const showSteps = !isRecipient && (step === "identify" || step === "recipient");

  return (
    <div className="ptp-page">
      <CustomerHeader />

      <main className="ptp-main">
        <span className="ptp-eyebrow">Package transfer</span>
        <h1 className="ptp-title">
          {isRecipient ? "You have been given a package" : "Give this package away"}
        </h1>
        <p className="ptp-lede">
          {isRecipient
            ? "Enter the code from the email to accept it. The package is not yours until you do, and nothing is charged to you."
            : "A package can be given away once, before any session has been used. When it moves, its number changes — so the number on your email stops working, and with it your access."}
        </p>

        {showSteps && (
          <ol className="ptp-steps">
            {senderSteps.map((entry, index) => (
              <React.Fragment key={entry.id}>
                {index > 0 && <li className="ptp-step-line" aria-hidden="true" />}
                <li
                  className={`ptp-step ${
                    index < currentIndex ? "ptp-step--done" : ""
                  } ${index === currentIndex ? "ptp-step--current" : ""}`}
                >
                  <span className="ptp-step-dot" aria-hidden="true">
                    {index < currentIndex ? "✓" : index + 1}
                  </span>
                  <span className="ptp-step-label">{entry.label}</span>
                </li>
              </React.Fragment>
            ))}
          </ol>
        )}

        <div className="ptp-card">
          {error && (
            <div className="ptp-alert ptp-alert--error" role="alert">
              {error}
            </div>
          )}

          {/* ── SENDER: prove it is yours ─────────────────────────────────────────────────── */}
          {step === "identify" && (
            <form onSubmit={goToRecipient}>
              <p className="ptp-hint" style={{ marginTop: 0, marginBottom: "18px" }}>
                Three details prove the package is yours. They are all on the email you received
                when you bought it.
              </p>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-email">
                  The email you bought with
                </label>
                <input
                  id="ptp-email"
                  className="ptp-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-phone">
                  Last 4 digits of your phone
                </label>
                <input
                  id="ptp-phone"
                  className="ptp-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={4}
                  value={phoneLast4}
                  onChange={(e) =>
                    setPhoneLast4(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  placeholder="••••"
                />
                <p className="ptp-hint">
                  The last 4 digits of the phone on this package. If the package was given to you, that is
                  the number you gave when you accepted it.
                </p>
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-number">
                  Package number
                </label>
                <input
                  id="ptp-number"
                  className="ptp-input ptp-input--number"
                  type="text"
                  inputMode="numeric"
                  maxLength={19}
                  value={groupDigits(accountNumber)}
                  onChange={(e) =>
                    setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 16))
                  }
                  placeholder="0000 0000 0000 0000"
                />
              </div>

              <button type="submit" className="ptp-btn" style={{ marginTop: "6px" }}>
                Continue
              </button>
            </form>
          )}

          {/* ── SENDER: who is receiving it ───────────────────────────────────────────────── */}
          {step === "recipient" && (
            <form onSubmit={startTransfer}>
              <div className="ptp-card-preview">
                <p className="ptp-preview-caption">The package you are giving away</p>
                {/* The REAL package when the sender is signed in: its number, its expiry, its sessions and the
                    shop. A guest's card stays number-only — the name is not known, and printing what they typed
                    as a cardholder would be inventing a fact. */}
                <PackageCard
                  accountNumber={
                    purchasePreview?.accountNumber || accountNumber
                  }
                  holderName={
                    [
                      purchasePreview?.customerFirstName,
                      purchasePreview?.customerLastName,
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  validUntil={purchasePreview?.validUntil}
                  totalSessions={purchasePreview?.totalSessions}
                  shopName={purchasePreview?.shopId?.name}
                  packageName={purchasePreview?.packageId?.name}
                  status={purchasePreview?.status}
                />
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-to">
                  Who is receiving it?
                </label>
                <input
                  id="ptp-to"
                  className="ptp-input"
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="their@email.com"
                />
                <p className="ptp-hint">
                  We send them a code and a link. They enter the code and their own phone number to
                  accept it — so a mistyped address costs a code and nothing else.
                </p>
              </div>

              {!confirming ? (
                <>
                  <button
                    type="button"
                    className="ptp-btn"
                    disabled={busy || !looksLikeEmail(toEmail)}
                    onClick={() => {
                      setError("");
                      setConfirming(true);
                    }}
                  >
                    Send them the code
                  </button>
                  <button
                    type="button"
                    className="ptp-btn ptp-btn--ghost"
                    disabled={busy}
                    onClick={() => {
                      setError("");
                      setConfirming(false);
                      setStep("identify");
                    }}
                  >
                    Back
                  </button>
                </>
              ) : (
                <div
                  className="ptp-confirm"
                  role="alertdialog"
                  aria-labelledby="ptp-sender-confirm-title"
                >
                  <p className="ptp-confirm-title" id="ptp-sender-confirm-title">
                    Give this package away?
                  </p>
                  <p className="ptp-confirm-text">
                    A code and a link go to <strong>{toEmail.trim()}</strong>. The moment they accept, the
                    package is theirs — your number stops working and you lose access. Nothing you do here
                    can be undone from this page.
                  </p>
                  <button type="submit" className="ptp-btn ptp-btn--confirm" disabled={busy}>
                    {busy ? "Sending…" : "Yes, send them the code"}
                  </button>
                  <button
                    type="button"
                    className="ptp-btn ptp-btn--ghost"
                    disabled={busy}
                    onClick={() => setConfirming(false)}
                  >
                    Not now
                  </button>
                </div>
              )}
            </form>
          )}

          {/* ── SENDER: sent, and that is the end of their part ───────────────────────────── */}
          {step === "sent" && (
            <div>
              <div className="ptp-done-mark" aria-hidden="true">
                ✓
              </div>
              <h2 className="ptp-title" style={{ textAlign: "center", fontSize: "21px" }}>
                We&apos;ve sent it to them
              </h2>
              <p className="ptp-done-text">
                <strong>{destination}</strong> has been sent a code and a link. The package moves when
                they enter it — nothing changes hands until then.
              </p>
              <div className="ptp-alert" role="status">
                If it has not arrived, ask them to check their spam folder. The code expires after ten
                minutes — after that, nothing has changed and the package is still yours.
              </div>
              {/* NO "SEND IT TO A DIFFERENT ADDRESS" HERE (user, 2026-10-09).

                  It read as a convenience and was the opposite of one. Every press sent ANOTHER code to
                  ANOTHER address, and a code is the thing that finishes the transfer — so the control that
                  looked like "fix a typo" was really "arm a second mailbox", on a page whose whole point is
                  that one named address receives it. The address is on screen above, in the line that says
                  where it went, which is what somebody who mistyped it actually needs: they can go back a
                  step deliberately rather than re-arm the transfer from a success screen. */}
            </div>
          )}

          {/* ── RECIPIENT: the code and their own number ──────────────────────────────────── */}
          {step === "activate" && (
            <form onSubmit={confirmTransfer}>
              <p className="ptp-hint" style={{ marginTop: 0, marginBottom: "18px" }}>
                Enter the code from the email, and your own phone number. The code proves you opened
                the email it was sent to; the number becomes the one the package is checked against.
              </p>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-code">
                  6-digit code
                </label>
                <input
                  id="ptp-code"
                  className="ptp-input ptp-input--code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                />
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="ptp-recipient-phone">
                  Your phone number
                </label>
                <input
                  id="ptp-recipient-phone"
                  className="ptp-input"
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                />
                {/* It has to be THEIRS: the number on file belonged to whoever gave the package away,
                    which is why a transferred package could not be opened by the person holding it.
                    A number typed by somebody else would look like a second factor while proving nothing. */}
                <p className="ptp-hint">
                  It must be yours, not the sender&apos;s. You will use its last 4 digits when you
                  book.
                </p>
              </div>

              {!confirming ? (
                <button
                  type="button"
                  className="ptp-btn ptp-btn--confirm"
                  disabled={busy || code.length !== 6 || !recipientPhone.trim()}
                  onClick={() => setConfirming(true)}
                >
                  Accept this package
                </button>
              ) : (
                <div
                  className="ptp-confirm"
                  role="alertdialog"
                  aria-labelledby="ptp-confirm-title"
                >
                  <p className="ptp-confirm-title" id="ptp-confirm-title">
                    Accept the package?
                  </p>
                  <p className="ptp-confirm-text">
                    It becomes yours and the sender loses access immediately. Only accept it if you
                    were expecting it.
                  </p>
                  <button
                    type="submit"
                    className="ptp-btn ptp-btn--confirm"
                    disabled={busy}
                  >
                    {busy ? "Accepting…" : "Yes, accept it"}
                  </button>
                  <button
                    type="button"
                    className="ptp-btn ptp-btn--ghost"
                    disabled={busy}
                    onClick={() => setConfirming(false)}
                  >
                    Not now
                  </button>
                </div>
              )}
            </form>
          )}

          {/* ── RECIPIENT: accepted ───────────────────────────────────────────────────────── */}
          {step === "done" && (
            <div>
              <div className="ptp-done-mark" aria-hidden="true">
                ✓
              </div>
              <h2 className="ptp-title" style={{ textAlign: "center", fontSize: "21px" }}>
                It&apos;s yours now
              </h2>
              <p className="ptp-done-text">
                The package has moved to <strong>{movedTo}</strong>. Its number has changed, so the
                sender&apos;s copy no longer works.
              </p>
              <div className="ptp-alert ptp-alert--success" role="status">
                <strong>Check your email:</strong> we have sent you the new package number. You will
                need it, this email address, and the last 4 digits of the phone you just gave.
              </div>
            </div>
          )}
        </div>
      </main>

      <CustomerFooter />
    </div>
  );
};

export default PackageTransferPage;
